import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Popover,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  ExitToApp as LogoutIcon,
  Settings as SettingsIcon,
  SignalWifi4Bar as ConnectedIcon,
  SignalWifiOff as DisconnectedIcon,
  Refresh as RefreshIcon,
  Help as HelpIcon,
  ManageAccounts as UsersIcon,
  VpnKey as ApiKeysIcon,
  NewReleases as WhatsNewIcon,
  AdminPanelSettings as PlatformAdminIcon,
  Security as AccountSecurityIcon,
  NotificationsNone as NotificationPrefsIcon,
  Tune as PreferencesIcon,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { format } from 'date-fns';
import type { RootState } from '@/store';
import { clearAuth } from '@/store/slices/authSlice';
import NotificationCenter from '@/components/notifications/NotificationCenter';
import { useWebSocket } from '@/hooks/useWebSocket';
import { fetchFacilities } from '@/store/slices/facilitiesSlice';
import { DemoBadge } from '@/components/DemoBadge';
import { operatorAppUrl } from '@/utils/operatorApp';

const DRAWER_WIDTH = 280;

interface HeaderProps {
  drawerOpen: boolean;
  onMenuClick: () => void;
  darkMode: boolean;
  onToggleDarkMode: () => void;
  facilityId?: string;
  /** Disable and close facility realtime for static configuration routes. */
  realtimeEnabled?: boolean;
}

/** Roles the facility settings route admits (router.tsx `settings`) plus SUPER_ADMIN, which passes every page gate. */
const SETTINGS_ROLES = ['ADMIN', 'SUPERVISOR', 'SUPER_ADMIN'];

export default function Header({
  drawerOpen,
  onMenuClick,
  darkMode,
  onToggleDarkMode,
  facilityId,
  realtimeEnabled = true,
}: HeaderProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [connAnchor, setConnAnchor] = useState<null | HTMLElement>(null);
  const [reconnecting, setReconnecting] = useState(false);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const user = useSelector((state: RootState) => state.auth.user);
  const connectionStatus = useSelector(
    (state: RootState) => state.realtime.connectionStatus,
  );
  const currentFacility = useSelector(
    (state: RootState) => state.facilities.currentFacility,
  );
  // Security fix C6: JWT no longer in Redux/localStorage. Drive WS auto-connect
  // off the auth flag — wsService fetches its own ws-token via the cookie.
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);

  const { reconnect, disconnect } = useWebSocket(facilityId ?? '', {
    autoConnect: Boolean(realtimeEnabled && facilityId && isAuthenticated),
  });

  useEffect(() => {
    if (!realtimeEnabled) {
      disconnect();
      setConnAnchor(null);
    }
  }, [disconnect, realtimeEnabled]);

  const handleLogout = () => {
    dispatch(clearAuth());
    navigate('/login');
  };

  const handleReconnect = async () => {
    if (!realtimeEnabled) return;
    setReconnecting(true);
    dispatch(fetchFacilities() as any);
    reconnect();
    reconnectTimeoutRef.current = setTimeout(() => {
      setReconnecting(false);
      setConnAnchor(null);
    }, 2000);
  };

  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current !== null) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  const title = currentFacility
    ? `${currentFacility.name} Dashboard`
    : 'Sentry Solutions';

  return (
    <AppBar
      component="header"
      role="banner"
      position="fixed"
      sx={{
        width: { sm: drawerOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%' },
        ml: { sm: drawerOpen ? `${DRAWER_WIDTH}px` : 0 },
        transition: (t) =>
          t.transitions.create(['margin', 'width'], {
            easing: t.transitions.easing.sharp,
            duration: t.transitions.duration.leavingScreen,
          }),
      }}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          edge="start"
          onClick={onMenuClick}
          aria-label={drawerOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={drawerOpen}
          aria-controls="facility-sidebar"
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>

        <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>
          {title}
        </Typography>

        {/* Demo badge */}
        {currentFacility?.is_demo && (
          <DemoBadge isDemo={true} className="mr-4" />
        )}

        {/* Connection status chip */}
        {facilityId && realtimeEnabled && (
          <>
            <Chip
              icon={
                connectionStatus.connected ? (
                  <ConnectedIcon sx={{ fontSize: 16, color: 'success.light' }} />
                ) : (
                  <DisconnectedIcon sx={{ fontSize: 16, color: 'error.light' }} />
                )
              }
              label={connectionStatus.connected ? 'Live' : 'Offline'}
              size="small"
              variant="outlined"
              onClick={(e) => setConnAnchor(e.currentTarget)}
              sx={{
                mr: 2,
                color: 'white',
                borderColor: connectionStatus.connected
                  ? 'success.light'
                  : 'error.light',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            />
            <Popover
              open={Boolean(connAnchor)}
              anchorEl={connAnchor}
              onClose={() => setConnAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <Box sx={{ p: 2, minWidth: 280 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Connection Status
                </Typography>
                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: connectionStatus.connected
                          ? 'success.main'
                          : 'error.main',
                      }}
                    />
                    <Typography variant="body2">
                      {connectionStatus.connected
                        ? 'Connected to real-time server'
                        : 'Disconnected from server'}
                    </Typography>
                  </Box>

                  {connectionStatus.lastConnected && (
                    <Typography variant="body2" color="text.secondary">
                      Last connected:{' '}
                      {format(
                        new Date(connectionStatus.lastConnected),
                        'MMM d, h:mm a',
                      )}
                    </Typography>
                  )}

                  {connectionStatus.reconnectAttempts != null &&
                    connectionStatus.reconnectAttempts > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Reconnect attempts: {connectionStatus.reconnectAttempts}
                      </Typography>
                    )}

                  {connectionStatus.error && (
                    <Typography variant="body2" color="error.main">
                      Error: {connectionStatus.error}
                    </Typography>
                  )}

                  {!connectionStatus.connected && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={
                        reconnecting ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <RefreshIcon />
                        )
                      }
                      onClick={handleReconnect}
                      disabled={reconnecting}
                      fullWidth
                    >
                      {reconnecting ? 'Reconnecting...' : 'Retry Connection'}
                    </Button>
                  )}

                  <Divider />
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mb: 1,
                      }}
                    >
                      <HelpIcon fontSize="small" color="action" />
                      <Typography variant="body2" fontWeight="medium">
                        Troubleshooting Tips
                      </Typography>
                    </Box>
                    {[
                      'Check your internet connection',
                      'Try refreshing the page',
                      'Ensure the backend server is running',
                      'Contact support if issues persist',
                    ].map((tip) => (
                      <Typography
                        key={tip}
                        variant="caption"
                        color="text.secondary"
                        component="div"
                      >
                        {`\u2022 ${tip}`}
                      </Typography>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Popover>
          </>
        )}

        {/* Dark mode toggle */}
        <IconButton
          color="inherit"
          onClick={onToggleDarkMode}
          aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          sx={{ ml: 1 }}
        >
          {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
        </IconButton>

        {/* Notifications */}
        <NotificationCenter facilityId={facilityId} />

        {/* User menu */}
        <Tooltip title="Account settings">
          <IconButton
            onClick={(e) => setUserMenuAnchor(e.currentTarget)}
            size="small"
            aria-label="Account settings"
            aria-haspopup="true"
            aria-expanded={Boolean(userMenuAnchor)}
            sx={{ ml: 1 }}
          >
            <Avatar sx={{ width: 32, height: 32 }}>
              {user?.name?.charAt(0).toUpperCase() ?? 'U'}
            </Avatar>
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={() => setUserMenuAnchor(null)}
          onClick={() => setUserMenuAnchor(null)}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem disabled>
            <Typography variant="body2">{user?.email}</Typography>
          </MenuItem>
          <Divider />
          {/* Only roles the settings route admits (ADMIN, SUPERVISOR; SUPER_ADMIN
              passes every page gate) see Settings (F17-16, decision S10). */}
          {SETTINGS_ROLES.includes((user?.role || '').toUpperCase()) && (
            <MenuItem
              onClick={() =>
                facilityId
                  ? navigate(`/facilities/${facilityId}/settings`)
                  : navigate('/facilities')
              }
            >
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              Settings
            </MenuItem>
          )}
          {['ADMIN', 'SUPER_ADMIN'].includes((user?.role || '').toUpperCase()) && (
            <MenuItem onClick={() => navigate('/users')}>
              <ListItemIcon>
                <UsersIcon fontSize="small" />
              </ListItemIcon>
              Manage Users
            </MenuItem>
          )}
          {['ADMIN', 'SUPER_ADMIN'].includes((user?.role || '').toUpperCase()) && (
            <MenuItem onClick={() => navigate('/settings/api-keys')}>
              <ListItemIcon>
                <ApiKeysIcon fontSize="small" />
              </ListItemIcon>
              API Keys
            </MenuItem>
          )}
          {user?.is_global === true && (
            <MenuItem onClick={() => window.location.assign(operatorAppUrl())}>
              <ListItemIcon>
                <PlatformAdminIcon fontSize="small" />
              </ListItemIcon>
              Platform Admin
            </MenuItem>
          )}
          {/* Personal account pages: every signed-in role (launch-readiness S7). */}
          <MenuItem
            onClick={() =>
              navigate(
                facilityId
                  ? `/facilities/${facilityId}/settings/account-security`
                  : '/account/security',
              )
            }
          >
            <ListItemIcon>
              <AccountSecurityIcon fontSize="small" />
            </ListItemIcon>
            Account security
          </MenuItem>
          <MenuItem
            onClick={() =>
              navigate(
                facilityId
                  ? `/facilities/${facilityId}/settings/notifications`
                  : '/account/notifications',
              )
            }
          >
            <ListItemIcon>
              <NotificationPrefsIcon fontSize="small" />
            </ListItemIcon>
            Notifications
          </MenuItem>
          {/* Every signed-in user: their own display units (decision S4). */}
          <MenuItem
            onClick={() =>
              navigate(
                facilityId
                  ? `/facilities/${facilityId}/settings/preferences`
                  : '/account/preferences',
              )
            }
          >
            <ListItemIcon>
              <PreferencesIcon fontSize="small" />
            </ListItemIcon>
            Units
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => navigate('/changelog')}>
            <ListItemIcon>
              <WhatsNewIcon fontSize="small" />
            </ListItemIcon>
            What&apos;s New
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
